import { createFileRoute } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "../lib/api";
import {
  fmtKr,
  fmtKrShort,
  fmtNum,
  ORG_META,
  type ApiKeyCreated,
  type ApiKeyInfo,
  type Company,
  type CompanyDetail,
  type CreateOpportunityPayload,
  type Customer,
  type Opportunity,
  type OrgId,
  type Pipeline,
} from "../lib/markadur";

export const Route = createFileRoute("/verkfaeri/markadurinn")({
  component: MarkadurPage,
});

type Tab = "pipeline" | "customers" | "search";
type TeamFilter = "all" | OrgId;

function MarkadurPage() {
  const [tab, setTab] = useState<Tab>("pipeline");
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [teamFilter, setTeamFilter] = useState<TeamFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);

  const reload = useCallback(() => {
    api
      .getPipeline(teamFilter === "all" ? undefined : teamFilter)
      .then(setPipeline)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, [teamFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const teams = pipeline?.teams ?? [];

  return (
    <div className="flex min-h-[calc(100vh-64px)]">
      <aside className="flex w-[248px] flex-none flex-col gap-1 border-r border-line bg-white px-4 py-[22px]">
        <div className="flex items-center gap-[11px] px-2.5 pb-4 pt-1.5">
          <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[11px] bg-[#FEF1E3] font-display text-[18px] font-extrabold text-[#F0871E]">
            ⇄
          </div>
          <div>
            <div className="font-display text-[14.5px] font-bold text-ink">
              Atomika CRM
            </div>
            <div className="text-[11.5px] text-muted-2">
              Sölupípa &amp; samningar
            </div>
          </div>
        </div>

        <NavItem
          active={tab === "pipeline"}
          onClick={() => setTab("pipeline")}
          label="Sölupípa"
        />
        <NavItem
          active={tab === "customers"}
          onClick={() => setTab("customers")}
          label="Viðskiptavinir"
        />
        <NavItem
          active={tab === "search"}
          onClick={() => setTab("search")}
          label="Fyrirtækjaleit"
        />

        <div className="flex-1" />

        <div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-4">
          Vigtuð sölupípa
        </div>
        {teams.map((t) => (
          <div
            key={t.org}
            className="mb-1.5 rounded-[14px] border border-line bg-canvas p-3"
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: ORG_META[t.org].color }}
              />
              <div className="text-[13px] font-bold text-ink">
                {ORG_META[t.org].label}
              </div>
            </div>
            <div
              className="mt-1 font-display text-[19px] font-extrabold"
              style={{ color: ORG_META[t.org].color }}
            >
              {fmtKrShort(t.openWeightedKr)}
            </div>
            <div className="text-[11px] text-muted-2">
              {t.openCount} opin · {t.wonCount} unnin
            </div>
          </div>
        ))}
      </aside>

      <div className="min-w-0 flex-1 px-[30px] pb-[56px] pt-[26px]">
        {error && (
          <div className="mb-4 rounded-xl border border-danger-50 bg-danger-50 px-4 py-3 text-[13px] font-semibold text-danger">
            {error}
          </div>
        )}

        {tab === "pipeline" && (
          <PipelineView
            pipeline={pipeline}
            teamFilter={teamFilter}
            onTeamFilter={setTeamFilter}
            onNew={() => setModal({ mode: "create" })}
            onEdit={(o) => setModal({ mode: "edit", opportunity: o })}
            onChanged={reload}
          />
        )}

        {tab === "customers" && <CustomersView />}

        {tab === "search" && (
          <SearchView
            onNewForCompany={(c) => setModal({ mode: "create", company: c })}
          />
        )}
      </div>

      {modal && (
        <OppModal
          state={modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function NavItem({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-[11px] rounded-[11px] px-3 py-[11px] text-left text-[13.5px] font-semibold ${
        active
          ? "bg-[#FEF1E3] font-bold text-[#B5651A]"
          : "text-muted hover:bg-canvas"
      }`}
    >
      {active && <span className="h-[7px] w-[7px] rounded-full bg-[#F0871E]" />}
      {label}
    </button>
  );
}

/* ------------------------------- Pipeline -------------------------------- */

function PipelineView({
  pipeline,
  teamFilter,
  onTeamFilter,
  onNew,
  onEdit,
  onChanged,
}: {
  pipeline: Pipeline | null;
  teamFilter: TeamFilter;
  onTeamFilter: (t: TeamFilter) => void;
  onNew: () => void;
  onEdit: (o: Opportunity) => void;
  onChanged: () => void;
}) {
  const filters: { id: TeamFilter; label: string }[] = [
    { id: "all", label: "Bæði teymin" },
    { id: "laufid", label: "Laufið" },
    { id: "atomika", label: "Atomika" },
  ];

  const stages = pipeline?.stages ?? [];
  const stageIds = stages.map((s) => s.id);

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex-1">
          <h1 className="m-0 font-display text-[28px] font-extrabold tracking-tight text-ink">
            Sölupípa
          </h1>
          <div className="mt-[3px] text-[13px] text-muted-2">
            Sameiginleg sölupípa Laufið og Atomika · vigtað virði = líkur % ×
            upphæð
          </div>
        </div>

        <div className="flex overflow-hidden rounded-[11px] border border-line bg-white">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onTeamFilter(f.id)}
              className={`px-3 py-[7px] text-[12.5px] font-bold ${
                teamFilter === f.id
                  ? "bg-brand text-white"
                  : "text-muted hover:bg-canvas"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onNew}
          className="rounded-[11px] bg-[#F0871E] px-4 py-[9px] text-[13px] font-bold text-white hover:opacity-90"
        >
          + Nýtt tækifæri
        </button>
      </div>

      {pipeline && <TeamStrip pipeline={pipeline} />}

      {!pipeline && (
        <div className="rounded-2xl border border-line bg-white px-5 py-16 text-center text-[14px] text-muted-2">
          Sæki sölupípu…
        </div>
      )}

      {pipeline && (
        <div className="flex gap-3.5 overflow-x-auto pb-3">
          {stages.map((stage, idx) => (
            <StageColumn
              key={stage.id}
              label={stage.label}
              opportunities={stage.opportunities}
              canBack={idx > 0}
              canForward={idx < stages.length - 1}
              onBack={(o) => advance(o, stageIds, -1, onChanged)}
              onForward={(o) => advance(o, stageIds, 1, onChanged)}
              onWon={(o) => setStatus(o, "won", onChanged)}
              onLost={(o) => setStatus(o, "lost", onChanged)}
              onEdit={onEdit}
            />
          ))}
          <ClosedColumn
            won={pipeline.won}
            lost={pipeline.lost}
            onReopen={(o) => setStatus(o, "open", onChanged)}
            onEdit={onEdit}
          />
        </div>
      )}
    </>
  );
}

function TeamStrip({ pipeline }: { pipeline: Pipeline }) {
  return (
    <div className="mb-[18px] grid grid-cols-2 gap-3.5">
      {pipeline.teams.map((t) => {
        const meta = ORG_META[t.org];
        return (
          <div
            key={t.org}
            className="rounded-2xl border border-line bg-white px-[18px] py-4"
            style={{ borderTop: `3px solid ${meta.color}` }}
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: meta.color }}
              />
              <div className="font-display text-[15px] font-bold text-ink">
                {meta.label}
              </div>
            </div>
            <div className="mt-2 flex items-end gap-6">
              <div>
                <div className="text-[11.5px] font-semibold text-muted-2">
                  Vigtað virði (opið)
                </div>
                <div
                  className="font-display text-[26px] font-extrabold"
                  style={{ color: meta.color }}
                >
                  {fmtKrShort(t.openWeightedKr)}
                </div>
              </div>
              <div className="pb-1">
                <div className="text-[11.5px] font-semibold text-muted-2">
                  Heild í pípu
                </div>
                <div className="font-display text-[15px] font-bold text-ink">
                  {fmtKrShort(t.openAmountKr)}
                </div>
              </div>
              <div className="pb-1">
                <div className="text-[11.5px] font-semibold text-muted-2">
                  Opin / unnin / töpuð
                </div>
                <div className="font-display text-[15px] font-bold text-ink">
                  {t.openCount} / {t.wonCount} / {t.lostCount}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StageColumn({
  label,
  opportunities,
  canBack,
  canForward,
  onBack,
  onForward,
  onWon,
  onLost,
  onEdit,
}: {
  label: string;
  opportunities: Opportunity[];
  canBack: boolean;
  canForward: boolean;
  onBack: (o: Opportunity) => void;
  onForward: (o: Opportunity) => void;
  onWon: (o: Opportunity) => void;
  onLost: (o: Opportunity) => void;
  onEdit: (o: Opportunity) => void;
}) {
  const weighted = opportunities.reduce((s, o) => s + o.weightedKr, 0);
  return (
    <div className="flex w-[266px] flex-none flex-col rounded-[16px] bg-[#F4F7FB] p-2.5">
      <div className="mb-2 flex items-center justify-between px-1.5 pt-1">
        <div className="font-display text-[13.5px] font-bold text-ink">
          {label}
        </div>
        <div className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-muted-2">
          {opportunities.length}
        </div>
      </div>
      <div className="mb-2.5 px-1.5 text-[11.5px] font-semibold text-muted-3">
        {fmtKrShort(weighted)} vigtað
      </div>
      <div className="flex flex-col gap-2.5">
        {opportunities.map((o) => (
          <OppCard
            key={o.id}
            o={o}
            canBack={canBack}
            canForward={canForward}
            onBack={() => onBack(o)}
            onForward={() => onForward(o)}
            onWon={() => onWon(o)}
            onLost={() => onLost(o)}
            onEdit={() => onEdit(o)}
          />
        ))}
        {opportunities.length === 0 && (
          <div className="rounded-xl border border-dashed border-line bg-white/60 px-3 py-5 text-center text-[12px] text-muted-3">
            Engin tækifæri
          </div>
        )}
      </div>
    </div>
  );
}

function OppCard({
  o,
  canBack,
  canForward,
  onBack,
  onForward,
  onWon,
  onLost,
  onEdit,
}: {
  o: Opportunity;
  canBack: boolean;
  canForward: boolean;
  onBack: () => void;
  onForward: () => void;
  onWon: () => void;
  onLost: () => void;
  onEdit: () => void;
}) {
  const meta = ORG_META[o.ownerOrg];
  return (
    <div className="rounded-[13px] border border-line bg-white p-3 shadow-[0_1px_2px_rgba(11,36,71,0.04)]">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="text-left font-display text-[13.5px] font-bold leading-tight text-ink hover:text-brand"
        >
          {o.companyName}
        </button>
        <span
          className="flex-none rounded-full px-2 py-0.5 text-[10.5px] font-bold"
          style={{ background: meta.bg, color: meta.color }}
        >
          {meta.label}
        </span>
      </div>

      {o.ownerUser && (
        <div className="mt-0.5 text-[11.5px] text-muted-2">{o.ownerUser}</div>
      )}

      <div className="mt-2 flex items-end justify-between">
        <div>
          <div className="font-display text-[15px] font-extrabold text-ink">
            {fmtKrShort(o.amountKr)}
          </div>
          <div className="text-[11px] text-muted-3">
            {o.probabilityPct}% · {fmtKrShort(o.weightedKr)} vigtað
          </div>
        </div>
      </div>

      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#EEF2F7]">
        <div
          className="h-full rounded-full"
          style={{ width: `${o.probabilityPct}%`, background: meta.color }}
        />
      </div>

      <div className="mt-2.5 flex items-center gap-1">
        <IconBtn disabled={!canBack} onClick={onBack} title="Fyrra stig">
          ◄
        </IconBtn>
        <IconBtn disabled={!canForward} onClick={onForward} title="Næsta stig">
          ►
        </IconBtn>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onWon}
          title="Merkja sem sölu"
          className="rounded-lg bg-ok-50 px-2 py-1 text-[11px] font-bold text-ok hover:opacity-80"
        >
          ✓ Sala
        </button>
        <button
          type="button"
          onClick={onLost}
          title="Merkja sem tapaða"
          className="rounded-lg bg-danger-50 px-2 py-1 text-[11px] font-bold text-danger hover:opacity-80"
        >
          ✕ Tap
        </button>
      </div>
    </div>
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="rounded-lg border border-line bg-white px-2 py-1 text-[11px] font-bold text-muted-2 hover:bg-canvas disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function ClosedColumn({
  won,
  lost,
  onReopen,
  onEdit,
}: {
  won: Opportunity[];
  lost: Opportunity[];
  onReopen: (o: Opportunity) => void;
  onEdit: (o: Opportunity) => void;
}) {
  const wonSum = won.reduce((s, o) => s + o.amountKr, 0);
  return (
    <div className="flex w-[266px] flex-none flex-col rounded-[16px] bg-[#F4F7FB] p-2.5">
      <div className="mb-2 flex items-center justify-between px-1.5 pt-1">
        <div className="font-display text-[13.5px] font-bold text-ink">
          Lokað
        </div>
        <div className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-muted-2">
          {won.length + lost.length}
        </div>
      </div>
      <div className="mb-2.5 px-1.5 text-[11.5px] font-semibold text-ok">
        {fmtKrShort(wonSum)} unnið
      </div>
      <div className="flex flex-col gap-2.5">
        {won.map((o) => (
          <ClosedCard
            key={o.id}
            o={o}
            won
            onReopen={() => onReopen(o)}
            onEdit={() => onEdit(o)}
          />
        ))}
        {lost.map((o) => (
          <ClosedCard
            key={o.id}
            o={o}
            won={false}
            onReopen={() => onReopen(o)}
            onEdit={() => onEdit(o)}
          />
        ))}
        {won.length + lost.length === 0 && (
          <div className="rounded-xl border border-dashed border-line bg-white/60 px-3 py-5 text-center text-[12px] text-muted-3">
            Ekkert lokað enn
          </div>
        )}
      </div>
    </div>
  );
}

function ClosedCard({
  o,
  won,
  onReopen,
  onEdit,
}: {
  o: Opportunity;
  won: boolean;
  onReopen: () => void;
  onEdit: () => void;
}) {
  const meta = ORG_META[o.ownerOrg];
  return (
    <div
      className="rounded-[13px] border bg-white p-3"
      style={{ borderColor: won ? "#CDEBDB" : "#F4D3D3" }}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="text-left font-display text-[13px] font-bold leading-tight text-ink hover:text-brand"
        >
          {o.companyName}
        </button>
        <span
          className="flex-none rounded-full px-2 py-0.5 text-[10px] font-bold"
          style={{
            background: won ? "#E7F7EF" : "#FDECEC",
            color: won ? "#16A75C" : "#E23B3B",
          }}
        >
          {won ? "Sala" : "Tap"}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <div className="text-[11.5px] text-muted-2">
          <span style={{ color: meta.color }}>{meta.label}</span>
          {o.ownerUser ? ` · ${o.ownerUser}` : ""}
        </div>
        <div className="font-display text-[13px] font-bold text-ink">
          {fmtKrShort(o.amountKr)}
        </div>
      </div>
      <button
        type="button"
        onClick={onReopen}
        className="mt-2 w-full rounded-lg border border-line bg-white px-2 py-1 text-[11px] font-semibold text-muted-2 hover:bg-canvas"
      >
        Opna aftur
      </button>
    </div>
  );
}

function advance(
  o: Opportunity,
  stageIds: string[],
  dir: number,
  done: () => void,
) {
  const idx = stageIds.indexOf(o.stage);
  const next = stageIds[idx + dir];
  if (!next) return;
  void api.updateOpportunity(o.id, { stage: next }).then(done);
}

function setStatus(
  o: Opportunity,
  status: "open" | "won" | "lost",
  done: () => void,
) {
  void api.updateOpportunity(o.id, { status }).then(done);
}

/* ---------------------------- Fyrirtækjaleit ----------------------------- */

function SearchView({
  onNewForCompany,
}: {
  onNewForCompany: (c: Company) => void;
}) {
  const [q, setQ] = useState("");
  const [total, setTotal] = useState(0);
  const [results, setResults] = useState<Company[]>([]);
  const [selected, setSelected] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setLoading(true);
      api
        .searchCompanies(q, 30)
        .then((r) => {
          setResults(r.results);
          setTotal(r.total);
        })
        .finally(() => setLoading(false));
    }, 220);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q]);

  const openCompany = (kt: string) => {
    api.getCompany(kt).then(setSelected);
  };

  return (
    <>
      <div className="mb-5">
        <h1 className="m-0 font-display text-[28px] font-extrabold tracking-tight text-ink">
          Fyrirtækjaleit
        </h1>
        <div className="mt-[3px] text-[13px] text-muted-2">
          {fmtNum(total)} íslensk fyrirtæki í skránni · leit eftir nafni,
          kennitölu eða stað
        </div>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Leita að fyrirtæki…"
        className="mb-4 w-full rounded-[13px] border border-line bg-white px-4 py-3 text-[14px] text-ink outline-none focus:border-brand"
      />

      <div className="grid grid-cols-[1.4fr_1fr] items-start gap-[18px]">
        <div className="overflow-hidden rounded-[16px] border border-line bg-white">
          <div className="flex items-center border-b border-[#F1F5FB] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.04em] text-muted-4">
            <div className="flex-[2]">Fyrirtæki</div>
            <div className="flex-1">Staður</div>
            <div className="flex-1 text-right">Starfsfólk</div>
          </div>
          {loading && results.length === 0 && (
            <div className="px-4 py-10 text-center text-[13px] text-muted-2">
              Leita…
            </div>
          )}
          {results.map((c) => (
            <button
              key={c.kennitala}
              type="button"
              onClick={() => openCompany(c.kennitala)}
              className={`flex w-full items-center border-b border-[#F5F8FC] px-4 py-3 text-left hover:bg-[#FAFCFF] ${
                selected?.company.kennitala === c.kennitala
                  ? "bg-[#FEF7EF]"
                  : ""
              }`}
            >
              <div className="flex-[2] min-w-0">
                <div className="truncate font-display text-[13.5px] font-bold text-ink">
                  {c.name}
                </div>
                <div className="truncate text-[11.5px] text-muted-2">
                  {c.isatLabel || c.legalForm}
                </div>
              </div>
              <div className="flex-1 truncate text-[12.5px] text-muted">
                {c.place || "—"}
              </div>
              <div className="flex-1 text-right font-display text-[13px] font-bold text-ink">
                {fmtNum(c.staffCount)}
              </div>
            </button>
          ))}
          {!loading && results.length === 0 && (
            <div className="px-4 py-10 text-center text-[13px] text-muted-2">
              Engin fyrirtæki fundust.
            </div>
          )}
        </div>

        <CompanyCard detail={selected} onNew={onNewForCompany} />
      </div>
    </>
  );
}

function CompanyCard({
  detail,
  onNew,
}: {
  detail: CompanyDetail | null;
  onNew: (c: Company) => void;
}) {
  if (!detail) {
    return (
      <div className="rounded-[16px] border border-dashed border-line bg-white px-5 py-16 text-center text-[13px] text-muted-2">
        Veldu fyrirtæki til að sjá spjald og árekstravörn.
      </div>
    );
  }

  const c = detail.company;
  return (
    <div className="rounded-[16px] border border-line bg-white p-5">
      <div className="font-display text-[19px] font-extrabold text-ink">
        {c.name}
      </div>
      <div className="mt-0.5 text-[12.5px] text-muted-2">
        {c.legalForm} · kt. {c.kennitala}
      </div>

      {detail.collision.engaged && (
        <div className="mt-3 rounded-xl border border-[#F6D9AE] bg-[#FEF3E4] px-3.5 py-2.5">
          <div className="text-[12.5px] font-bold text-[#A9701F]">
            ⚠ Árekstravörn
          </div>
          <div className="mt-0.5 text-[12px] text-[#A9701F]">
            {detail.collision.orgs.map((o) => ORG_META[o].label).join(" og ")}{" "}
            {detail.collision.orgs.length > 1 ? "eru" : "er"} þegar með opið
            tækifæri hjá þessu fyrirtæki.
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Field label="Starfsfólk" value={fmtNum(c.staffCount)} />
        <Field label="Stærð" value={c.sizeClass || "—"} />
        <Field label="Staður" value={c.place || "—"} />
        <Field label="Landshluti" value={c.region || "—"} />
        <Field
          label="Rekstrartekjur"
          value={c.revenue ? fmtKr(c.revenue) : "—"}
        />
        <Field label="ISAT" value={c.isatLabel || "—"} />
        <Field label="Sími" value={c.phone || "—"} />
        <Field label="Netfang" value={c.email || "—"} />
      </div>

      {detail.opportunities.length > 0 && (
        <div className="mt-4">
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
            Sölutækifæri ({detail.opportunities.length})
          </div>
          {detail.opportunities.map((o) => (
            <div
              key={o.id}
              className="mb-1.5 flex items-center justify-between rounded-xl bg-canvas px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: ORG_META[o.ownerOrg].color }}
                />
                <span className="text-[12.5px] font-semibold text-ink">
                  {ORG_META[o.ownerOrg].label}
                </span>
                <span className="text-[11.5px] text-muted-2">
                  {o.status === "open"
                    ? `${o.probabilityPct}%`
                    : o.status === "won"
                      ? "Sala"
                      : "Tap"}
                </span>
              </div>
              <div className="font-display text-[12.5px] font-bold text-ink">
                {fmtKrShort(o.amountKr)}
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => onNew(c)}
        className="mt-4 w-full rounded-[12px] bg-[#F0871E] px-4 py-[10px] text-[13px] font-bold text-white hover:opacity-90"
      >
        + Stofna sölutækifæri
      </button>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-muted-3">{label}</div>
      <div className="text-[13px] font-semibold text-ink">{value}</div>
    </div>
  );
}

/* ------------------------------- Modal ----------------------------------- */

type ModalState =
  | { mode: "create"; company?: Company }
  | { mode: "edit"; opportunity: Opportunity };

function OppModal({
  state,
  onClose,
  onSaved,
}: {
  state: ModalState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state.mode === "edit" ? state.opportunity : null;
  const initialCompany = state.mode === "create" ? state.company : undefined;

  const [company, setCompany] = useState<{
    kennitala: string;
    name: string;
  } | null>(
    editing
      ? { kennitala: editing.companyKennitala, name: editing.companyName }
      : initialCompany
        ? { kennitala: initialCompany.kennitala, name: initialCompany.name }
        : null,
  );
  const [ownerOrg, setOwnerOrg] = useState<OrgId>(
    editing?.ownerOrg ?? "atomika",
  );
  const [ownerUser, setOwnerUser] = useState(editing?.ownerUser ?? "");
  const [title, setTitle] = useState(editing?.title ?? "");
  const [amount, setAmount] = useState<number>(editing?.amountKr ?? 5_000_000);
  const [prob, setProb] = useState<number>(editing?.probabilityPct ?? 30);
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const weighted = Math.round(amount * (prob / 100));

  const save = () => {
    if (!company) {
      setErr("Veldu fyrirtæki");
      return;
    }
    setSaving(true);
    setErr(null);
    const run = editing
      ? api.updateOpportunity(editing.id, {
          title,
          ownerUser,
          amountKr: amount,
          probabilityPct: prob,
          notes,
        })
      : api.createOpportunity({
          companyKennitala: company.kennitala,
          companyName: company.name,
          title: title || undefined,
          ownerOrg,
          ownerUser: ownerUser || undefined,
          amountKr: amount,
          probabilityPct: prob,
          notes: notes || undefined,
        } satisfies CreateOpportunityPayload);
    run
      .then(onSaved)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setSaving(false));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] rounded-[20px] bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="m-0 font-display text-[20px] font-extrabold text-ink">
            {editing ? "Breyta tækifæri" : "Nýtt sölutækifæri"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-[16px] text-muted-2 hover:bg-canvas"
          >
            ✕
          </button>
        </div>

        {!editing && (
          <div className="mb-3">
            <Label>Fyrirtæki</Label>
            {company ? (
              <div className="flex items-center justify-between rounded-[12px] border border-line bg-canvas px-3.5 py-2.5">
                <span className="font-display text-[14px] font-bold text-ink">
                  {company.name}
                </span>
                <button
                  type="button"
                  onClick={() => setCompany(null)}
                  className="text-[12px] font-semibold text-muted-2 hover:text-brand"
                >
                  Breyta
                </button>
              </div>
            ) : (
              <CompanyPicker onPick={setCompany} />
            )}
          </div>
        )}

        {editing && (
          <div className="mb-3 rounded-[12px] border border-line bg-canvas px-3.5 py-2.5 font-display text-[14px] font-bold text-ink">
            {editing.companyName}
          </div>
        )}

        {!editing && (
          <div className="mb-3">
            <Label>Teymi</Label>
            <div className="flex overflow-hidden rounded-[12px] border border-line">
              {(["laufid", "atomika"] as OrgId[]).map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOwnerOrg(o)}
                  className={`flex-1 px-3 py-2.5 text-[13px] font-bold ${
                    ownerOrg === o ? "text-white" : "bg-white text-muted"
                  }`}
                  style={
                    ownerOrg === o
                      ? { background: ORG_META[o].color }
                      : undefined
                  }
                >
                  {ORG_META[o].label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <Label>Sölumaður</Label>
            <input
              value={ownerUser}
              onChange={(e) => setOwnerUser(e.target.value)}
              placeholder="Nafn"
              className="w-full rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none focus:border-brand"
            />
          </div>
          <div>
            <Label>Heiti tækifæris</Label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Valfrjálst"
              className="w-full rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none focus:border-brand"
            />
          </div>
        </div>

        <div className="mb-3">
          <Label>Upphæð (kr)</Label>
          <input
            type="number"
            value={amount}
            min={0}
            step={100000}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
            className="w-full rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[14px] font-semibold text-ink outline-none focus:border-brand"
          />
        </div>

        <div className="mb-3">
          <Label>Líkur á sölu: {prob}%</Label>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={prob}
            onChange={(e) => setProb(Number(e.target.value))}
            className="w-full"
          />
        </div>

        <div className="mb-3">
          <Label>Athugasemd</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Valfrjálst — næstu skref, tengiliður, o.s.frv."
            rows={2}
            className="w-full resize-none rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[13px] text-ink outline-none focus:border-brand"
          />
        </div>

        <div className="mb-4 rounded-[12px] bg-[#FEF1E3] px-4 py-3">
          <div className="text-[11.5px] font-semibold text-[#A9701F]">
            Vigtað virði (líkur × upphæð)
          </div>
          <div className="font-display text-[22px] font-extrabold text-[#B5651A]">
            {fmtKr(weighted)}
          </div>
        </div>

        {err && (
          <div className="mb-3 rounded-lg bg-danger-50 px-3 py-2 text-[12.5px] font-semibold text-danger">
            {err}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[12px] border border-line bg-white px-4 py-2.5 text-[13px] font-bold text-muted hover:bg-canvas"
          >
            Hætta við
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex-1 rounded-[12px] bg-[#F0871E] px-4 py-2.5 text-[13px] font-bold text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving
              ? "Vista…"
              : editing
                ? "Vista breytingar"
                : "Stofna tækifæri"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CompanyPicker({
  onPick,
}: {
  onPick: (c: { kennitala: string; name: string }) => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Company[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      api.searchCompanies(q, 8).then((r) => setResults(r.results));
    }, 200);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q]);

  return (
    <div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Leita að fyrirtæki…"
        className="w-full rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none focus:border-brand"
      />
      {results.length > 0 && (
        <div className="mt-1.5 max-h-[180px] overflow-y-auto rounded-[12px] border border-line">
          {results.map((c) => (
            <button
              key={c.kennitala}
              type="button"
              onClick={() => onPick({ kennitala: c.kennitala, name: c.name })}
              className="flex w-full items-center justify-between border-b border-[#F5F8FC] px-3 py-2 text-left last:border-0 hover:bg-canvas"
            >
              <span className="truncate text-[13px] font-semibold text-ink">
                {c.name}
              </span>
              <span className="ml-2 flex-none text-[11.5px] text-muted-2">
                {c.place}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 text-[11.5px] font-bold uppercase tracking-[0.05em] text-muted-4">
      {children}
    </div>
  );
}

/* ----------------------------- Viðskiptavinir ---------------------------- */

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("is-IS");
}

function CustomersView() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filter, setFilter] = useState<"all" | "active">("all");
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    api
      .getMarkadurCustomers()
      .then((r) => setCustomers(r.customers))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const activeCount = customers.filter((c) => c.activated).length;
  const shown =
    filter === "active" ? customers.filter((c) => c.activated) : customers;
  const selectedCustomer =
    customers.find((c) => c.kennitala === selected) ?? null;

  const filters: { id: "all" | "active"; label: string }[] = [
    { id: "all", label: `Allir (${customers.length})` },
    { id: "active", label: `Virku (${activeCount})` },
  ];

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex-1">
          <h1 className="m-0 font-display text-[28px] font-extrabold tracking-tight text-ink">
            Viðskiptavinir
          </h1>
          <div className="mt-[3px] text-[13px] text-muted-2">
            Fyrirtæki með unnið tilboð verða viðskiptavinir · virkjaðu þau til
            að tengja mælaborð með API-lykli
          </div>
        </div>

        <div className="flex overflow-hidden rounded-[11px] border border-line bg-white">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-[7px] text-[12.5px] font-bold ${
                filter === f.id
                  ? "bg-brand text-white"
                  : "text-muted hover:bg-canvas"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-danger-50 bg-danger-50 px-4 py-3 text-[13px] font-semibold text-danger">
          {error}
        </div>
      )}

      {loading && customers.length === 0 && (
        <div className="rounded-2xl border border-line bg-white px-5 py-16 text-center text-[14px] text-muted-2">
          Sæki viðskiptavini…
        </div>
      )}

      {!loading && customers.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-white px-5 py-16 text-center text-[14px] text-muted-2">
          Engir viðskiptavinir enn. Merktu tilboð sem „Sala" í Sölupípunni til
          að búa til viðskiptavin.
        </div>
      )}

      {customers.length > 0 && (
        <div className="grid grid-cols-[1.2fr_1fr] items-start gap-[18px]">
          <div className="overflow-hidden rounded-[16px] border border-line bg-white">
            <div className="flex items-center border-b border-[#F1F5FB] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.04em] text-muted-4">
              <div className="flex-[2]">Fyrirtæki</div>
              <div className="flex-1 text-right">Unnið virði</div>
              <div className="flex-1 text-right">Staða</div>
            </div>
            {shown.map((c) => (
              <CustomerRow
                key={c.kennitala}
                c={c}
                active={selected === c.kennitala}
                onClick={() => setSelected(c.kennitala)}
              />
            ))}
            {shown.length === 0 && (
              <div className="px-4 py-10 text-center text-[13px] text-muted-2">
                Engir virkir viðskiptavinir enn.
              </div>
            )}
          </div>

          <CustomerDetail customer={selectedCustomer} onChanged={reload} />
        </div>
      )}
    </>
  );
}

function CustomerRow({
  c,
  active,
  onClick,
}: {
  c: Customer;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center border-b border-[#F5F8FC] px-4 py-3 text-left hover:bg-[#FAFCFF] ${
        active ? "bg-[#FEF7EF]" : ""
      }`}
    >
      <div className="min-w-0 flex-[2]">
        <div className="truncate font-display text-[13.5px] font-bold text-ink">
          {c.name}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          {c.ownerOrgs.map((o) => (
            <span
              key={o}
              className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
              style={{ background: ORG_META[o].bg, color: ORG_META[o].color }}
            >
              {ORG_META[o].label}
            </span>
          ))}
          <span className="truncate text-[11.5px] text-muted-2">
            {c.place || "—"}
          </span>
        </div>
      </div>
      <div className="flex-1 text-right font-display text-[13px] font-bold text-ink">
        {fmtKrShort(c.wonAmountKr)}
      </div>
      <div className="flex flex-1 justify-end">
        {c.activated ? (
          <span className="rounded-full bg-ok-50 px-2 py-0.5 text-[10.5px] font-bold text-ok">
            Virkur
          </span>
        ) : (
          <span className="rounded-full bg-canvas px-2 py-0.5 text-[10.5px] font-bold text-muted-2">
            Í bið
          </span>
        )}
      </div>
    </button>
  );
}

function CustomerDetail({
  customer,
  onChanged,
}: {
  customer: Customer | null;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kt = customer?.kennitala ?? null;

  useEffect(() => {
    setDetail(null);
    if (!kt) return;
    api
      .getCompany(kt)
      .then(setDetail)
      .catch(() => {});
  }, [kt]);

  if (!customer) {
    return (
      <div className="rounded-[16px] border border-dashed border-line bg-white px-5 py-16 text-center text-[13px] text-muted-2">
        Veldu viðskiptavin til að sjá fyrirtækjaspjald, virkja hann og búa til
        API-lykil fyrir mælaborðið.
      </div>
    );
  }

  const c = detail?.company;

  const setActive = (activate: boolean) => {
    setBusy(true);
    setError(null);
    const run = activate
      ? api.activateCustomer(customer.kennitala)
      : api.deactivateCustomer(customer.kennitala);
    run
      .then(() => onChanged())
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="rounded-[16px] border border-line bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-[19px] font-extrabold text-ink">
            {customer.name}
          </div>
          <div className="mt-0.5 text-[12.5px] text-muted-2">
            {c?.legalForm ? `${c.legalForm} · ` : ""}kt. {customer.kennitala}
          </div>
        </div>
        {customer.activated ? (
          <span className="flex-none rounded-full bg-ok-50 px-2.5 py-1 text-[11px] font-bold text-ok">
            ✓ Virkur
          </span>
        ) : (
          <span className="flex-none rounded-full bg-canvas px-2.5 py-1 text-[11px] font-bold text-muted-2">
            Í bið
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Field label="Staður" value={c?.place || customer.place || "—"} />
        <Field label="Landshluti" value={c?.region || "—"} />
        <Field label="Starfsfólk" value={fmtNum(c?.staffCount ?? null)} />
        <Field
          label="Rekstrartekjur"
          value={c?.revenue ? fmtKr(c.revenue) : "—"}
        />
        <Field label="Sími" value={c?.phone || "—"} />
        <Field label="Netfang" value={c?.email || "—"} />
        <Field
          label="Unnin tækifæri"
          value={`${customer.wonCount} · ${fmtKrShort(customer.wonAmountKr)}`}
        />
        <Field label="Síðasta sala" value={fmtDate(customer.lastWonAt)} />
      </div>

      {error && (
        <div className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-[12.5px] font-semibold text-danger">
          {error}
        </div>
      )}

      <div className="mt-4 rounded-[12px] border border-line bg-canvas p-3.5">
        {customer.activated ? (
          <div className="flex items-center justify-between gap-3">
            <div className="text-[12.5px] text-muted-2">
              Virkur síðan {fmtDate(customer.activatedAt)}
              {customer.activatedBy ? ` · ${customer.activatedBy}` : ""}
            </div>
            <button
              type="button"
              onClick={() => setActive(false)}
              disabled={busy}
              className="rounded-lg border border-line bg-white px-3 py-1.5 text-[12px] font-semibold text-muted-2 hover:bg-canvas disabled:opacity-50"
            >
              Afvirkja
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="text-[12.5px] text-muted-2">
              Virkjaðu viðskiptavininn til að tengja mælaborð og búa til
              API-lykil.
            </div>
            <button
              type="button"
              onClick={() => setActive(true)}
              disabled={busy}
              className="flex-none rounded-lg bg-ok px-3.5 py-1.5 text-[12.5px] font-bold text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Virkja…" : "Virkja viðskiptavin"}
            </button>
          </div>
        )}
      </div>

      {customer.activated && (
        <>
          <ApiKeysPanel
            kennitala={customer.kennitala}
            companyName={customer.name}
            onChanged={onChanged}
          />

          <a
            href={`/verkfaeri/atomika-maelabord?customer=${encodeURIComponent(customer.kennitala)}`}
            className="mt-4 flex w-full items-center justify-center rounded-[12px] bg-ink px-4 py-[10px] text-[13px] font-bold text-white hover:opacity-90"
          >
            Fara í mælaborð
            {customer.dashboardConnected ? " · tengt ✓" : " · bíð eftir gögnum"}
          </a>
        </>
      )}
    </div>
  );
}

function ApiKeysPanel({
  kennitala,
  companyName,
  onChanged,
}: {
  kennitala: string;
  companyName: string;
  onChanged: () => void;
}) {
  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    api
      .listApiKeys(kennitala)
      .then(setKeys)
      .catch(() => {});
  }, [kennitala]);

  useEffect(() => {
    setCreated(null);
    reload();
  }, [reload]);

  const create = () => {
    setBusy(true);
    setError(null);
    api
      .createApiKey({
        name: `${companyName} — Atomika ingest`,
        ownerOrg: "atomika",
        scopes: "ingest,read",
        customerKennitala: kennitala,
      })
      .then((k) => {
        setCreated(k);
        reload();
        onChanged();
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setBusy(false));
  };

  const revoke = (id: number) => {
    void api.revokeApiKey(id).then(() => {
      reload();
      onChanged();
    });
  };

  const remove = (id: number) => {
    void api.deleteApiKey(id).then(() => {
      reload();
      onChanged();
    });
  };

  return (
    <div className="mt-4 rounded-[12px] border border-line p-3.5">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
          API-lyklar (mælaborð)
        </div>
        <button
          type="button"
          onClick={create}
          disabled={busy}
          className="rounded-lg bg-[#F0871E] px-3 py-1.5 text-[12px] font-bold text-white hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Bý til…" : "+ Búa til lykil"}
        </button>
      </div>

      {error && (
        <div className="mb-2 rounded-lg bg-danger-50 px-3 py-2 text-[12px] font-semibold text-danger">
          {error}
        </div>
      )}

      {created && (
        <div className="mb-3 rounded-lg border border-[#F6D9AE] bg-[#FEF3E4] px-3 py-2.5">
          <div className="text-[11.5px] font-bold text-[#A9701F]">
            Nýr lykill — afritaðu núna, hann sést aðeins einu sinni:
          </div>
          <code className="mt-1 block break-all rounded bg-white px-2 py-1.5 font-mono text-[11.5px] text-ink">
            {created.key}
          </code>
        </div>
      )}

      {keys.length === 0 && !created && (
        <div className="rounded-lg bg-canvas px-3 py-4 text-center text-[12px] text-muted-2">
          Enginn API-lykill enn. Búðu til lykil svo Atomika geti ýtt mælingum
          inn.
        </div>
      )}

      {keys.map((k) => (
        <div
          key={k.id}
          className="mb-1.5 flex items-center justify-between gap-2 rounded-lg bg-canvas px-3 py-2 last:mb-0"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <code className="font-mono text-[12px] font-bold text-ink">
                {k.prefix}…
              </code>
              {k.active ? (
                <span className="rounded-full bg-ok-50 px-1.5 py-0.5 text-[10px] font-bold text-ok">
                  Virkur
                </span>
              ) : (
                <span className="rounded-full bg-danger-50 px-1.5 py-0.5 text-[10px] font-bold text-danger">
                  Afturkallaður
                </span>
              )}
            </div>
            <div className="truncate text-[11px] text-muted-2">
              {k.scopes} · {fmtDate(k.createdAt)}
            </div>
          </div>
          <div className="flex flex-none items-center gap-1">
            {k.active && (
              <button
                type="button"
                onClick={() => revoke(k.id)}
                className="rounded-md border border-line bg-white px-2 py-1 text-[11px] font-semibold text-muted-2 hover:bg-white"
              >
                Afturkalla
              </button>
            )}
            <button
              type="button"
              onClick={() => remove(k.id)}
              title="Eyða lykli"
              className="rounded-md border border-line bg-white px-2 py-1 text-[11px] font-semibold text-danger hover:bg-danger-50"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
